/**
 * AMTasks — Task management page for Account Managers
 *
 * Toggle between Kanban and List views.
 * Filter by priority, client, source.
 * Create/edit tasks via dialog.
 */

import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AMLayout } from "@/components/am/AMLayout";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useAMTasks, type AMTask, type TaskStatus } from "@/hooks/useAMTasks";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, LayoutGrid, List, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import TaskKanban from "@/components/am/TaskKanban";
import TaskList from "@/components/am/TaskList";
import TaskFormDialog from "@/components/am/TaskFormDialog";

type ViewMode = "kanban" | "list";

interface ClientOption {
  agentId: string;
  name: string;
}

export default function AMTasks() {
  const { accountManager, loading: amLoading } = useAccountManager();
  const { tasks, loading: tasksLoading, createTask, updateTask, moveTask, deleteTask } = useAMTasks(
    accountManager?.id || null
  );
  const [searchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterClient, setFilterClient] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<AMTask | null>(null);

  // Client list for the form selector
  const [clients, setClients] = useState<ClientOption[]>([]);

  // Pre-select client from URL param (e.g., /am/tasks?client=xxx)
  const _urlClientId = searchParams.get("client");

  useEffect(() => {
    if (!accountManager) return;

    const fetchClients = async () => {
      const { data: assignments } = await supabase
        .from("client_am_assignments")
        .select("agent_id")
        .eq("am_id", accountManager.id)
        .eq("status", "active");

      if (!assignments || assignments.length === 0) return;

      const agentIds = assignments.map((a) => a.agent_id);
      const { data: agents } = await supabase
        .from("agents")
        .select("id, name")
        .in("id", agentIds);

      if (agents) {
        setClients(agents.map((a) => ({ agentId: a.id, name: a.name || "Unknown" })));
      }
    };

    fetchClients();
  }, [accountManager]);

  // Build agent name lookup
  const agentNames = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((c) => map.set(c.agentId, c.name));
    return map;
  }, [clients]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filterPriority !== "all" && t.priority !== filterPriority) return false;
      if (filterClient !== "all" && t.agentId !== filterClient) return false;
      if (filterSource !== "all" && t.source !== filterSource) return false;
      return true;
    });
  }, [tasks, filterPriority, filterClient, filterSource]);

  // Stats
  const todoCount = tasks.filter((t) => t.status === "todo").length;
  const inProgressCount = tasks.filter((t) => t.status === "in_progress").length;
  const overdueCount = tasks.filter(
    (t) => t.dueDate && t.status !== "done" && new Date(t.dueDate) < new Date()
  ).length;

  const handleTaskClick = (task: AMTask) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setDialogOpen(true);
  };

  const handleSave = async (data: {
    title: string;
    description?: string;
    agentId?: string;
    priority: string;
    status: string;
    dueDate?: string;
  }) => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, {
          title: data.title,
          description: data.description || null,
          agentId: data.agentId || null,
          priority: data.priority as any,
          status: data.status as TaskStatus,
          dueDate: data.dueDate || null,
        });
        toast.success("Task updated");
      } else {
        await createTask({
          title: data.title,
          description: data.description,
          agentId: data.agentId,
          priority: data.priority as any,
          dueDate: data.dueDate,
        });
        toast.success("Task created");
      }
    } catch {
      toast.error("Failed to save task");
    }
  };

  const handleDelete = editingTask
    ? async () => {
        try {
          await deleteTask(editingTask.id);
          toast.success("Task deleted");
        } catch {
          toast.error("Failed to delete task");
        }
      }
    : undefined;

  const handleToggleDone = async (taskId: string, currentStatus: TaskStatus) => {
    const newStatus = currentStatus === "done" ? "todo" : "done";
    await moveTask(taskId, newStatus);
  };

  if (amLoading || tasksLoading) {
    return (
      <AMLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AMLayout>
    );
  }

  return (
    <AMLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Tasks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your client tasks and follow-ups
            </p>
          </div>
          <Button onClick={handleNewTask} className="gap-2">
            <Plus className="h-4 w-4" />
            New Task
          </Button>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <Clock className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-2xl font-bold">{todoCount}</p>
                <p className="text-xs text-muted-foreground">To Do</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <CheckCircle2 className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{inProgressCount}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <AlertTriangle className={`h-5 w-5 ${overdueCount > 0 ? "text-red-500" : "text-muted-foreground"}`} />
              <div>
                <p className={`text-2xl font-bold ${overdueCount > 0 ? "text-red-600" : ""}`}>{overdueCount}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters + View Toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-40 h-8 text-xs">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.agentId} value={c.agentId}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="auto_health">Health</SelectItem>
              <SelectItem value="auto_sla">SLA</SelectItem>
              <SelectItem value="auto_contract">Contract</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex gap-1">
            <Button
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("kanban")}
              className="h-8 px-2"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-8 px-2"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Board / List */}
        {viewMode === "kanban" ? (
          <TaskKanban
            tasks={filteredTasks}
            agentNames={agentNames}
            onTaskClick={handleTaskClick}
            onMoveTask={moveTask}
          />
        ) : (
          <Card>
            <CardContent className="pt-4">
              <TaskList
                tasks={filteredTasks}
                agentNames={agentNames}
                onTaskClick={handleTaskClick}
                onToggleDone={handleToggleDone}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Task Form Dialog */}
      <TaskFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editingTask}
        clients={clients}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </AMLayout>
  );
}
